'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { trackCardUpClick } from '@libs/analytics';
import { useSitePreviewData } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import type { CardUpVariantType } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import CardUpVariant from './CardUpVariant';
import type { Card, CardUpBrandProps, CardUpOfferProps, CardUpProps } from './types';
import { prepareEligibleOffers } from './utils';
import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';
const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
  ssr: false
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
  ssr: false
});
const CardUpOfferVariant = dynamic(() => import('./card-up-offer-variant'), {
  ssr: false
});
const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
  '2up': props => <CardUpVariant sliceMax={2} {...props} />,
  '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...props as unknown as CardUpBrandProps} />,
  '3up': props => <CardUpVariant sliceMax={3} {...props} />,
  '4up': props => <CardUpVariant sliceMax={4} {...props} />,
  '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
  '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
  Info: props => <CardUpVariant sliceMax={4} {...props} />,
  Offer: props => <CardUpOfferVariant {...props as unknown as CardUpOfferProps} />
};
const validateCardTypes = (cards: Card[]): boolean => {
  const articleModel = cards.some(card => card.previewDescription);
  const articleModelsAll = cards.every(card => card.previewDescription);
  return articleModel && !articleModelsAll;
};
const getEligibleCards = (variant: CardUpVariantType, cards: Card[], previewDate?: string): Card[] | Offer[] => {
  if (variant === 'Offer') {
    return prepareEligibleOffers(cards ?? [], previewDate ? new Date(previewDate) : new Date());
  }
  return cards;
};
export default function CardUp(t0) {
  const $ = _c(52);
  const {
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1
  } = t0;
  const pathname = usePathname();
  const previewDate = useSitePreviewData()?.previewDate;
  let t1;
  if ($[0] !== cards || $[1] !== previewDate || $[2] !== variant) {
    t1 = getEligibleCards(variant, cards, previewDate);
    $[0] = cards;
    $[1] = previewDate;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cardsToRender = t1;
  if (!cardsToRender?.length) {
    return null;
  }
  const Component = ComponentCardUp[variant];
  if (!Component) {
    return null;
  }
  if (variant === "3up Article" && validateCardTypes(cards)) {
    return null;
  }
  let t2;
  if ($[4] !== linkAction) {
    t2 = linkAction || {};
    $[4] = linkAction;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    link,
    actionText,
    internalTitle
  } = t2;
  let t3;
  if ($[6] !== header) {
    t3 = getTrimmedDisplayTitle(header);
    $[6] = header;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const displayHeader = t3;
  let t4;
  if ($[8] !== header || $[9] !== isH1) {
    t4 = getPlpModuleSectionHeading(header, isH1);
    $[8] = header;
    $[9] = isH1;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    title: sectionTitle,
    headingTagName
  } = t4;
  let t5;
  if ($[11] !== actionText || $[12] !== displayHeader || $[13] !== header || $[14] !== internalTitle || $[15] !== link || $[16] !== linkActionColor || $[17] !== variant) {
    t5 = displayHeader && link && actionText ? <ColoredTextLink data-testid="link-action" onClick={() => {
      trackCardUpClick({
        variant,
        title: header,
        cardName: actionText,
        cardPosition: -1,
        promoDesc: internalTitle ?? ""
      });
    }} href={link?.webUrl} color={linkActionColor}>{actionText}</ColoredTextLink> : null;
    $[11] = actionText;
    $[12] = displayHeader;
    $[13] = header;
    $[14] = internalTitle;
    $[15] = link;
    $[16] = linkActionColor;
    $[17] = variant;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const titleAfter = t5;
  let isRestyled;
  let t6;
  let variantName;
  if ($[19] !== displayHeader || $[20] !== variant) {
    variantName = variant.toLowerCase().replaceAll(" ", "-");
    const noHeaderClass = !displayHeader ? `${variantName}--no-header` : "";
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = ["2up", "3up", "3up Article", "4up", "Info", "4Stack", "Offer"];
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    const restyledVariants = t7;
    isRestyled = restyledVariants.includes(variant);
    t6 = cn("card-up", variantName, noHeaderClass, isRestyled && "card-up--restyled");
    $[19] = displayHeader;
    $[20] = variant;
    $[21] = isRestyled;
    $[22] = t6;
    $[23] = variantName;
  } else {
    isRestyled = $[21];
    t6 = $[22];
    variantName = $[23];
  }
  const componentClassName = t6;
  const cardHeaderTagName = isH1 || !displayHeader ? "h2" : "h3";
  const t7 = `card-up-container card-up-container--${variantName}`;
  let t8;
  if ($[25] !== backgroundColor) {
    t8 = {
      backgroundColor
    };
    $[25] = backgroundColor;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== isRestyled) {
    t9 = isRestyled && {
      align: "center",
      titleAfterLocation: "below-description"
    };
    $[27] = isRestyled;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = cardsToRender as Card[];
  const t11 = pathname ?? "";
  let t12;
  if ($[29] !== Component || $[30] !== cardHeaderTagName || $[31] !== description || $[32] !== header || $[33] !== isH1 || $[34] !== linkAction || $[35] !== t10 || $[36] !== t11 || $[37] !== variant) {
    t12 = <Component header={header} description={description} linkAction={linkAction} cards={t10} variant={variant} pathname={t11} isH1={isH1} cardHeaderTagName={cardHeaderTagName} />;
    $[29] = Component;
    $[30] = cardHeaderTagName;
    $[31] = description;
    $[32] = header;
    $[33] = isH1;
    $[34] = linkAction;
    $[35] = t10;
    $[36] = t11;
    $[37] = variant;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== componentClassName || $[40] !== description || $[41] !== headingTagName || $[42] !== sectionTitle || $[43] !== t12 || $[44] !== t8 || $[45] !== t9 || $[46] !== titleAfter || $[47] !== variantName) {
    t13 = <Section data-testid="card-up" data-variant={variantName} className={componentClassName} title={sectionTitle} description={description} titleAfter={titleAfter} style={t8} headingTagName={headingTagName} {...t9}>{t12}</Section>;
    $[39] = componentClassName;
    $[40] = description;
    $[41] = headingTagName;
    $[42] = sectionTitle;
    $[43] = t12;
    $[44] = t8;
    $[45] = t9;
    $[46] = titleAfter;
    $[47] = variantName;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t13 || $[50] !== t7) {
    t14 = <LayoutContainer className={t7}>{t13}</LayoutContainer>;
    $[49] = t13;
    $[50] = t7;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  return t14;
}